import { Box, EftWithdrawTransactionResponse } from 'vopay';
import { FunctionComponent } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TransactionCard from '@/components/Entities/UI/TransactionCard/TransactionCard';
import TransactionDetailsCard from '@/components/Entities/UI/TransactionDetailsCard/TransactionDetailsCard';
import ErrorInfoCard from '@/components/Entities/UI/ErrorInfoCard/ErrorInfoCard';

type EftWithdrawTransactionInfoProps = {
  transaction: EftWithdrawTransactionResponse;
};

type FC = FunctionComponent<EftWithdrawTransactionInfoProps>;

const EftWithdrawTransactionInfo: FC = ({ transaction }) => {
  const { accountId } = useParams<{ accountId: string }>();
  const accountName = new URLSearchParams(useLocation().search).get('accountName') || '';

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.Details.TransactionDetails',
  });

  const isRecipientNameIdentity = Boolean(transaction.FirstName && transaction.LastName);

  const transactionDetails = [
    {
      label: t('Label.transactionType', { defaultValue: 'Transaction Type' }),
      value: t('TransactionType.eftWithdrawal', { defaultValue: 'EFT Withdrawal' }),
    },
    {
      label: t('Label.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('Label.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime || '-',
    },
    {
      label: t('Label.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified || '-',
    },
    {
      label: t('Label.holdReleaseDateTime', { defaultValue: 'Hold Release Date Time' }),
      value: transaction.HoldReleaseDateTime || '-',
    },
    {
      label: t('Label.amount', { defaultValue: 'Amount' }),
      value: transaction.Amount || '-',
    },
    {
      label: t('Label.currency', { defaultValue: 'Currency' }),
      value: transaction.Currency || '-',
    },
    {
      label: t('Label.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount || '-',
    },
    {
      label: t('Label.scheduledTransactionId', { defaultValue: 'Scheduled Transaction ID' }),
      value: transaction.ScheduledTransactionID || '-',
    },
    {
      label: t('Label.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
  ];

  const nameAndAddressDetails = [
    {
      label: t('Label.recipientName', { defaultValue: 'Recipient Name' }),
      value: isRecipientNameIdentity ? `${transaction.FirstName} ${transaction.LastName}` : '-',
    },
    {
      label: t('Label.companyName', { defaultValue: 'Company Name' }),
      value: transaction.CompanyName || '-',
    },
    {
      label: t('Label.dateOfBirth', { defaultValue: 'Date of Birth' }),
      value: transaction.DOB || '-',
    },
    {
      label: t('Label.phoneNumber', { defaultValue: 'Phone Number' }),
      value: transaction.PhoneNumber || '-',
    },
    {
      label: t('Label.email', { defaultValue: 'Email' }),
      value: transaction.Email || '-',
    },
    {
      label: t('Label.address1', { defaultValue: 'Address 1' }),
      value: transaction.Address1 || '-',
    },
    {
      label: t('Label.address2', { defaultValue: 'Address 2' }),
      value: transaction.Address2 || '-',
    },
    {
      label: t('Label.city', { defaultValue: 'City' }),
      value: transaction.City || '-',
    },
    {
      label: t('Label.province', { defaultValue: 'Province' }),
      value: transaction.Province || '-',
    },
    {
      label: t('Label.country', { defaultValue: 'Country' }),
      value: transaction.Country || '-',
    },
    {
      label: t('Label.postalCode', { defaultValue: 'Postal Code' }),
      value: transaction.PostalCode || '-',
    },
  ];

  const bankingDetails = [
    {
      label: t('Label.financialInstitutionNumber', { defaultValue: 'Financial Institution Number' }),
      value: transaction.FinancialInstitutionNumber || '-',
    },
    {
      label: t('Label.branchTransitNumber', { defaultValue: 'Branch Transit Number' }),
      value: transaction.BranchTransitNumber || '-',
    },
    {
      label: t('Label.accountNumber', { defaultValue: 'Account Number' }),
      value: transaction.AccountNumber || '-',
    },
    {
      label: t('Label.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={accountName || '-'}
        amount={transaction.Amount}
        currency={transaction.Currency}
        transactionId={transaction.TransactionID}
        type="EFT Withdrawal"
        accountId={accountId}
        failureReason={transaction.FailureReason}
      />

      <TransactionDetailsCard
        title={t('Title.transactionDetails', { defaultValue: 'Transaction Details' })}
        details={transactionDetails}
      />

      <TransactionDetailsCard
        title={t('Title.nameAndAddress', { defaultValue: 'Name and Address' })}
        details={nameAndAddressDetails}
      />

      <TransactionDetailsCard
        title={t('Title.bankingInformation', { defaultValue: 'Banking Information' })}
        details={bankingDetails}
      />

      <ErrorInfoCard
        currency={transaction.Currency}
        amount={transaction.Amount}
        lastModified={transaction.LastModified}
        failureReason={transaction.FailureReason}
      />
    </Box>
  );
};

export default EftWithdrawTransactionInfo;
